import { useEffect, useState } from "react";
import { ArrowRight, Copy, ImageOff, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { PublicAppTemplate } from "@/lib/app-templates";

export function TemplatePreviewDialog({
  template,
  actionsDisabled,
  creating,
  onOpenChange,
  onCopyLink,
  onCreate,
}: {
  template: PublicAppTemplate | null;
  actionsDisabled: boolean;
  creating: boolean;
  onOpenChange: (open: boolean) => void;
  onCopyLink: () => void;
  onCreate: () => void;
}) {
  const [previewError, setPreviewError] = useState(false);

  useEffect(() => {
    setPreviewError(false);
  }, [template?.id, template?.preview_image_url]);

  const showImage = Boolean(template?.preview_image_url) && !previewError;

  return (
    <Dialog open={template !== null} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-6xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b px-5 py-4 pr-12 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <DialogTitle className="line-clamp-2 leading-6">
                {template?.name ?? "Template preview"}
              </DialogTitle>
              <DialogDescription
                className={template?.description
                  ? "mt-1 line-clamp-2 max-w-4xl font-medium leading-6 text-foreground/75"
                  : "sr-only"}
              >
                {template?.description || "Template preview"}
              </DialogDescription>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
              <Button type="button" variant="outline" onClick={onCopyLink} disabled={!template}>
                <Copy className="mr-2 h-4 w-4" aria-hidden="true" />
                Copy link
              </Button>
              <Button
                type="button"
                onClick={onCreate}
                disabled={!template || actionsDisabled}
              >
                {creating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="mr-2 h-4 w-4" />
                )}
                Use Template
              </Button>
            </div>
          </div>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-muted/40 p-3 sm:p-6">
          {showImage && template?.preview_image_url ? (
            <img
              src={template.preview_image_url}
              alt={`${template.name} large App preview`}
              className="max-h-full w-auto max-w-full rounded-lg border border-border bg-white object-contain shadow-sm"
              decoding="async"
              draggable={false}
              onError={() => setPreviewError(true)}
            />
          ) : (
            <div className="flex min-h-48 flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
              <ImageOff className="h-6 w-6" aria-hidden="true" />
              Preview image
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
